import { useEffect, useMemo, useState } from "react";
import {
  FileTextIcon,
  LayoutTemplateIcon,
  RefreshCwIcon,
  SearchIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { type TemplateDto, useTemplates } from "@/hooks/use-templates";
import { openDocumentInSidebar } from "@/lib/threadDocumentOps";
import { openPraetorCaseDocument } from "@/lib/authFetch";
import { usePdfDocumentStore } from "@/components/pdf/pdfDocumentStore";
import { useAuth } from "@/lib/auth/AuthContext";
import { cn } from "@/lib/utils";
import { SettingsPageHeader } from "./settings-page-header";
import type { BreadcrumbSegment } from "./settings-page-header";

export function TemplatesList() {
  const { t } = useTranslation();
  const { templates, loading, error, refresh } = useTemplates();
  const { scheme } = useAuth();
  const [query, setQuery] = useState("");

  // Leaving the Templates section clears the document sidebar — template
  // previews are transient and must not bleed into the chat sidebar state.
  useEffect(() => {
    return () => usePdfDocumentStore.getState().closeAllDocuments();
  }, []);

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredTemplates = useMemo(
    () =>
      templates.filter((template) => {
        if (!normalizedQuery) return true;
        return template.fileName.toLocaleLowerCase().includes(normalizedQuery);
      }),
    [templates, normalizedQuery],
  );

  const showEmptyFilterState =
    normalizedQuery.length > 0 && filteredTemplates.length === 0;
  const showEmptyState =
    normalizedQuery.length === 0 && filteredTemplates.length === 0;

  const showOpenInPraetor = scheme === "Bearer";

  const breadcrumbs: BreadcrumbSegment[] = [
    { labelKey: "settings.breadcrumb.root", href: "/settings" },
    { labelKey: "settings.nav.templates" },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-8 pt-8 pb-6">
        <div className="mx-auto max-w-[880px] space-y-6">
          <SettingsPageHeader
            breadcrumbs={breadcrumbs}
            titleKey="settings.templates.title"
            subtitleKey="settings.templates.subtitle"
          />

          <div className="relative">
            <SearchIcon
              className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.7}
            />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("settings.templates.filterPlaceholder")}
              aria-label={t("settings.templates.filterPlaceholder")}
              className="h-11 w-full rounded-[10px] border border-border bg-background pr-4 pl-10 text-[14px] outline-none transition-shadow placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_3px_oklch(0.55_0.03_264/0.10)]"
            />
          </div>

          {error && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={refresh}>
                <RefreshCwIcon className="size-4" />
                {t("common.retry")}
              </Button>
            </div>
          )}

          {loading && templates.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-[13px] text-muted-foreground">
              <Spinner size="sm" />
              <span>{t("common.loading")}</span>
            </div>
          ) : showEmptyFilterState ? (
            <EmptyState
              icon={SearchIcon}
              label={t("settings.templates.emptyFilterState")}
            />
          ) : showEmptyState ? (
            <EmptyState
              icon={LayoutTemplateIcon}
              label={t("settings.templates.emptyState")}
            />
          ) : (
            <div className="grid gap-3">
              {filteredTemplates.map((template) => (
                <TemplateRow
                  key={template.templateId}
                  template={template}
                  showOpenInPraetor={showOpenInPraetor}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateRow({
  template,
  showOpenInPraetor,
}: {
  template: TemplateDto;
  showOpenInPraetor: boolean;
}) {
  const { t } = useTranslation();

  const openPreview = () => {
    void openDocumentInSidebar(template.templateId);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openPreview}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPreview();
        }
      }}
      className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-border/60 bg-background px-4 py-4 transition-colors hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <FileTextIcon
          className="size-[18px] shrink-0 text-muted-foreground"
          strokeWidth={1.7}
        />
        <h2 className="truncate font-medium text-[15px] text-foreground">
          {template.fileName}
        </h2>
      </div>

      {showOpenInPraetor && (
        <TooltipIconButton
          tooltip={t("settings.templates.openInPraetor")}
          side="bottom"
          className={cn(
            "size-8 shrink-0 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer",
          )}
          onClick={(event) => {
            event.stopPropagation();
            openPraetorCaseDocument(template.templateId);
          }}
        >
          <img
            src="/open-icon.png"
            alt=""
            className="size-4 rounded object-cover"
          />
        </TooltipIconButton>
      )}
    </div>
  );
}

function EmptyState({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-border/80 border-dashed bg-muted/10 px-6 py-14 text-center">
      <Icon className="size-8 text-muted-foreground" strokeWidth={1.7} />
      <p className="mt-4 max-w-md text-[13px] text-muted-foreground">{label}</p>
    </div>
  );
}
